import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

import cn from 'classnames';
import Heading from 'components/shared/heading';
import Button from 'components/shared/button';
import PhoneIcon from 'components/icons/Phone';
import ArrowRightIcon from 'components/icons/ArrowRight';
import ArrowLeftIcon from 'components/icons/ArrowLeftMini';
import ArrowRightCarouselIcon from 'components/icons/ArrowRightMini';
import Link from 'components/shared/link';
import styles from './styles.module.scss';

const HorizontalCarousel = ({
  title = null,
  subTitle = null,
  theme = null,
  buttonLabel = null,
  buttonLink = null,
  secondaryButtonLink = null,
  secondaryButtonLabel = null,
  autoPlay = false,
  playDuration = 2500,
}) => {
  const containerRef = useRef(null);
  const [imageList, setImageList] = useState([]);
  const autoplayPlugin = useMemo(
    () =>
      autoPlay
        ? Autoplay({
            delay: playDuration || 3000,
            stopOnInteraction: false,
            stopOnMouseEnter: true,
          })
        : null,
    [autoPlay, playDuration]
  );
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: 'start',
      slidesToScroll: 1,
      containScroll: 'keepSnaps',
    },
    autoplayPlugin ? [autoplayPlugin] : []
  );

  useEffect(() => {
    if (containerRef?.current) {
      // find in parent div with class name 'imageListBox' and get all divs inside it
      const parent = containerRef?.current?.parentElement;
      const imagesBox = parent.querySelector('.imageListBox');
      setTimeout(() => {
        if (imagesBox) {
          const imgs = Array.from(
            imagesBox?.children ||
              imagesBox?.querySelectorAll('picture') ||
              imagesBox?.querySelectorAll('img[data-main-image]') ||
              []
          );
          setImageList(imgs);
        }
      }, 400);
    }
  }, []);

  // Re-initialize embla when images are loaded
  useEffect(() => {
    if (emblaApi && imageList.length > 0) {
      emblaApi.reInit();
    }
  }, [emblaApi, imageList]);

  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setPrevBtnEnabled(emblaApi.canScrollPrev());
    setNextBtnEnabled(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return undefined;

    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  const hasImages = useMemo(() => !!(imageList && imageList.length > 0), [imageList]);

  const isPhone = useMemo(() => buttonLink?.includes('tel:'), [buttonLink]);
  const isSecondaryButtonPhone = useMemo(
    () => secondaryButtonLink?.includes('tel:'),
    [secondaryButtonLink]
  );

  return (
    <section
      className={cn(styles.mediaWrapper, {
        [styles.themeLight]: theme === 'light',
        [styles.themeDark]: theme === 'dark',
      })}
      ref={containerRef}
    >
      {hasImages && (
        <div className={styles.inner}>
          <div className="container">
            {title && (
              <Heading className={styles.title} tag="h3" size="xl">
                {title}
              </Heading>
            )}
            {subTitle && (
              <div className={styles.subTitle}>
                <p>{subTitle}</p>
              </div>
            )}

            <div className={cn(styles.carouselBox)}>
              <div className={styles.emblaWrapper}>
                <button
                  type="button"
                  className={cn(styles.emblaButton, styles.emblaButtonPrev, {
                    [styles.emblaButtonDisabled]: !prevBtnEnabled,
                  })}
                  onClick={scrollPrev}
                  disabled={!prevBtnEnabled}
                  aria-label="Previous slide"
                >
                  <ArrowLeftIcon />
                </button>
                <div className={styles.embla} ref={emblaRef}>
                  <div className={styles.emblaContainer}>
                    {imageList.map((image, index) => (
                      <div className={styles.emblaSlide} key={index}>
                        <div
                          className={styles.slide}
                          dangerouslySetInnerHTML={{ __html: image.outerHTML }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  className={cn(styles.emblaButton, styles.emblaButtonNext, {
                    [styles.emblaButtonDisabled]: !nextBtnEnabled,
                  })}
                  onClick={scrollNext}
                  disabled={!nextBtnEnabled}
                  aria-label="Next slide"
                >
                  <ArrowRightCarouselIcon />
                </button>
              </div>
            </div>
            {buttonLabel && buttonLink && (
              <div className={styles.btnBox}>
                {/* {buttonHint && <p className={styles.btnHint}>{buttonHint}</p>} */}
                <Button
                  theme="accent-primary"
                  to={buttonLink}
                  className={cn(styles.btn, { [styles.btnPhone]: isPhone })}
                >
                  {isPhone && <PhoneIcon />}
                  {buttonLabel}
                  {!isPhone && <ArrowRightIcon />}
                </Button>
              </div>
            )}
            {secondaryButtonLink && secondaryButtonLabel && (
              <div className={styles.secondaryBtnBox}>
                <Link to={secondaryButtonLink} className={styles.secondaryBtn}>
                  {isSecondaryButtonPhone && <PhoneIcon />}
                  <span>{secondaryButtonLabel}</span>
                  {!isSecondaryButtonPhone && <ArrowRightIcon />}
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

HorizontalCarousel.propTypes = {
  title: PropTypes.string,
  subTitle: PropTypes.string,
  theme: PropTypes.oneOf(['light']),
  buttonLabel: PropTypes.string,
  buttonLink: PropTypes.string,
  secondaryButtonLink: PropTypes.string,
  secondaryButtonLabel: PropTypes.string,
  autoPlay: PropTypes.oneOf([true, false, 1]),
  playDuration: PropTypes.number,
};

export default HorizontalCarousel;
